// admin-ui/src/main.jsx
import React, { useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
} from 'react-router-dom'

import {
  AuthProvider,
  useAuthRole,
} from './auth/AuthProvider.jsx'

import RequireRole from './auth/RequireRole.jsx'

import BPage from './features/b/BPage.jsx'
import CListPage from './features/c/pages/CListPage.jsx'
import EditorPage from './features/c/pages/EditorPage.jsx'
import AuthCallback from './routes/AuthCallback.jsx'

import './index.css'

function Home() {
  const {
    user,
    userRole,
    loading,
    isAuthenticated,
    isPrivileged,
    signInWithMagicLink,
    signOut,
    authError,
  } = useAuthRole()

  const [email, setEmail] = useState('')
  const [loginPending, setLoginPending] = useState(false)
  const [loginSent, setLoginSent] = useState(false)
  const [localError, setLocalError] = useState(null)

  async function handleLogin(event) {
    event.preventDefault()

    setLocalError(null)
    setLoginSent(false)

    if (!email.trim()) {
      setLocalError('Bitte E-Mail-Adresse eingeben.')
      return
    }

    try {
      setLoginPending(true)

      await signInWithMagicLink(email)

      setLoginSent(true)
    } catch (error) {
      console.error('[Home] Magic-Link Login fehlgeschlagen:', error)

      setLocalError(
        error?.message || 'Login konnte nicht gestartet werden.',
      )
    } finally {
      setLoginPending(false)
    }
  }

  async function handleLogout() {
    try {
      await signOut()
    } catch (error) {
      console.error('[Home] Logout fehlgeschlagen:', error)

      setLocalError(
        error?.message || 'Logout fehlgeschlagen.',
      )
    }
  }

  if (loading) {
    return (
      <main
        style={{
          maxWidth: 800,
          margin: '0 auto',
          padding: 16,
        }}
      >
        <p>Anmeldung wird geprüft …</p>
      </main>
    )
  }

  /*
   * Nicht eingeloggt:
   * zentrale Magic-Link Login-Seite
   */
  if (!isAuthenticated) {
    return (
      <main
        style={{
          maxWidth: 520,
          margin: '0 auto',
          padding: 16,
        }}
      >
        <h1>W2H Admin</h1>

        <p>
          Bitte melde dich mit deiner E-Mail-Adresse an.
        </p>

        <form onSubmit={handleLogin}>
          <label htmlFor="email">
            E-Mail-Adresse
          </label>

          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              setLoginSent(false)
              setLocalError(null)
            }}
            placeholder="name@example.com"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: 10,
              marginTop: 8,
              marginBottom: 12,
            }}
          />

          <button
            type="submit"
            disabled={loginPending}
            style={{
              padding: '10px 16px',
              cursor: loginPending ? 'default' : 'pointer',
            }}
          >
            {loginPending
              ? 'Magic Link wird gesendet …'
              : 'Magic Link senden'}
          </button>
        </form>

        {loginSent && (
          <p style={{ marginTop: 16 }}>
            Der Login-Link wurde gesendet. Bitte prüfe dein
            E-Mail-Postfach.
          </p>
        )}

        {(localError || authError) && (
          <p
            style={{
              marginTop: 16,
              color: '#b00020',
            }}
          >
            {localError || authError?.message}
          </p>
        )}
      </main>
    )
  }

  /*
   * Admin / Editor:
   * komplettes Admin-Menü
   */
  const privilegedItems = [
    {
      key: 'A',
      external: true,
      href: 'https://tool.wind2horizon.com/',
      label: 'Modul A – Import-Tool',
    },
    {
      key: 'B',
      to: '/admin',
      label: 'Modul B – Marker frei setzen',
    },
    {
      key: 'C',
      to: '/c',
      label: 'Modul C – Redaktion',
    },
  ]

  /*
   * Normaler User / Partner:
   * aktuell nur Modul C.
   *
   * Später ersetzen wir das durch:
   * "Meine Einträge"
   */
  const userItems = [
    {
      key: 'C',
      to: '/c',
      label: 'Meine Einträge',
    },
  ]

  const items = isPrivileged
    ? privilegedItems
    : userItems

  return (
    <main
      style={{
        maxWidth: 800,
        margin: '0 auto',
        padding: 16,
      }}
    >
      <h1>W2H Admin</h1>

      <p>
        Eingeloggt als <strong>{user?.email}</strong>
      </p>

      <p>
        Rolle:{' '}
        <strong>
          {userRole || 'user'}
        </strong>
      </p>

      <p>
        {isPrivileged
          ? 'Wähle ein Modul:'
          : 'Deine verfügbaren Bereiche:'}
      </p>

      <nav aria-label="Module">
        <ul
          style={{
            lineHeight: 1.8,
            listStyle: 'disc',
            paddingLeft: 24,
          }}
        >
          {items.map((item) => (
            <li key={item.key}>
              {item.external ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.label}
                </a>
              ) : (
                <Link to={item.to}>
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <button
        type="button"
        onClick={handleLogout}
        style={{
          marginTop: 24,
          padding: '10px 16px',
          cursor: 'pointer',
        }}
      >
        Logout
      </button>

      {localError && (
        <p
          style={{
            marginTop: 16,
            color: '#b00020',
          }}
        >
          {localError}
        </p>
      )}
    </main>
  )
}

createRoot(
  document.getElementById('root'),
).render(
  <React.StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={<Home />}
          />

          {/* Supabase Magic-Link Callback */}
          <Route
            path="/auth/callback"
            element={<AuthCallback />}
          />

          {/* Modul B: nur admin/editor */}
          <Route
            path="/admin"
            element={
              <RequireRole
                allowedRoles={[
                  'admin',
                  'editor',
                ]}
              >
                <BPage />
              </RequireRole>
            }
          />

          {/* Modul C: jeder eingeloggte User */}
          <Route
            path="/c"
            element={
              <RequireRole>
                <CListPage />
              </RequireRole>
            }
          />

          <Route
            path="/c/:id"
            element={
              <RequireRole>
                <EditorPage />
              </RequireRole>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </React.StrictMode>,
)